import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../auth/AuthContext";
import { useAdminService } from "../../data/adminService";
import { useAsyncData } from "../../hooks/useAsyncData";
import { formatNumberDe } from "../../utils/format";
import "./shell.css";

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Fixed 248px sidebar: logo lockup, ÜBERSICHT nav (Users carries a live total
 * badge), and the admin/logout panel pinned to the bottom. Kampagnen/Referral/
 * Settings are disabled until their placeholder screens land (FE-6/7).
 */
export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { logout, email, name } = useAuthContext();
  const service = useAdminService();
  // The badge only reads `totalUsers`, which is window-independent — pass any
  // valid [from, to) span (last 24h) just to satisfy the required bounds.
  const { data: kpis } = useAsyncData(() => {
    const now = new Date();
    const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return service.getDashboardKpis({
      range: "WEEK",
      from: from.toISOString(),
      to: now.toISOString(),
    });
  }, [service]);

  const displayName = name || email || "Admin";

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <div className="sidebar-logo-mark">
          <img src="/topbar_logo.svg" alt="Lyra" />
        </div>
        <div className="sidebar-logo-text">
          <span className="sidebar-logo-title">{t("app.name")}</span>
          <span className="sidebar-logo-sub">{t("app.consoleName")}</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        <span className="sidebar-section">{t("nav.section")}</span>

        <NavLink
          to="/dashboard"
          className={({ isActive }) => `nav-item${isActive ? " nav-item--active" : ""}`}
        >
          {t("nav.dashboard")}
        </NavLink>

        <NavLink
          to="/users"
          className={({ isActive }) => `nav-item${isActive ? " nav-item--active" : ""}`}
        >
          {t("nav.users")}
          {kpis && <span className="nav-count">{formatNumberDe(kpis.totalUsers)}</span>}
        </NavLink>

        <NavLink
          to="/campaigns"
          className={({ isActive }) => `nav-item${isActive ? " nav-item--active" : ""}`}
        >
          {t("nav.kampagnen")}
        </NavLink>

        <NavLink
          to="/referral"
          className={({ isActive }) => `nav-item${isActive ? " nav-item--active" : ""}`}
        >
          {t("nav.referral")}
          <span className="nav-badge">{t("nav.soon")}</span>
        </NavLink>

        <NavLink
          to="/settings"
          className={({ isActive }) => `nav-item${isActive ? " nav-item--active" : ""}`}
        >
          {t("nav.settings")}
        </NavLink>
      </nav>

      <button className="sidebar-user" onClick={logout} title={t("auth.signOut")}>
        <span className="sidebar-user-avatar">{initialsOf(displayName)}</span>
        <span className="sidebar-user-meta">
          <span className="sidebar-user-name">{displayName}</span>
          <span className="sidebar-user-role">{t("topbar.adminRole")}</span>
        </span>
        <SignOutIcon />
      </button>
    </aside>
  );
};

const SignOutIcon: React.FC = () => (
  <svg className="sidebar-user-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h11" />
  </svg>
);
